import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import { fetchOrderById, cancelOrder, confirmDelivery } from '@/store/slices/ordersSlice';
import { Order } from '@/types';
import { ROUTES } from '@/utils/constants';
import { getPrimaryImage, getPlaceholderImage } from '@/utils/imageUtils';
import { formatCurrency } from '@/utils/formatters';
import Button from '@/components/ui/Button';
import {
  ArrowLeftIcon,
  TruckIcon,
  CreditCardIcon,
  MapPinIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import OrderTrackingTimeline from '@/components/order/OrderTrackingTimeline';
import toast from 'react-hot-toast';

const OrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentOrder, isLoading, error } = useSelector((state: RootState) => state.orders);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    if (id) {
      const orderId = Number(id);
      // Check if we already have this order in state (e.g., just created)
      if (currentOrder && currentOrder.id === orderId) {
        // Order is already loaded, no need to fetch
        return;
      }
      // Otherwise, fetch the order
      dispatch(fetchOrderById(orderId));
    }
  }, [dispatch, id, currentOrder]);

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }
    
    try {
      await dispatch(cancelOrder(currentOrder!.id)).unwrap();
      setShowCancelModal(false);
      setCancelReason('');
      toast.success('Order cancelled successfully');
      // Refresh order data
      if (id) {
        dispatch(fetchOrderById(Number(id)));
      }
    } catch (error: any) {
      toast.error(error || 'Failed to cancel order');
    }
  };

  const handleConfirmDelivery = async () => {
    if (window.confirm('Confirm that you have received your order?')) {
      try {
        await dispatch(confirmDelivery(currentOrder!.id)).unwrap();
        toast.success('Delivery confirmed successfully');
        // Refresh order data
        if (id) {
          dispatch(fetchOrderById(Number(id)));
        }
      } catch (error: any) {
        toast.error(error || 'Failed to confirm delivery');
      }
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <CheckCircleIcon className="h-6 w-6 text-green-500" />;
      case 'cancelled':
        return <XCircleIcon className="h-6 w-6 text-red-500" />;
      default:
        return <ClockIcon className="h-6 w-6 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading order...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Order</h2>
          <p className="text-gray-600 mb-6">
            {typeof error === 'string' ? error : 'Failed to load order details. Please try again.'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!currentOrder) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📦</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Order Not Found</h2>
          <p className="text-gray-600 mb-6">The order you're looking for doesn't exist or you don't have permission to view it.</p>
          <button
            onClick={() => navigate('/buyer/orders')}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }


  const order = currentOrder;

  return (
    <div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Order Details</h1>
            <p className="mt-2 text-gray-600">Order #{order.order_number}</p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => navigate(ROUTES.ORDERS)}
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Orders
          </Button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Order Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Status */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Order Status</h2>
                <div className="flex items-center space-x-2">
                  {getStatusIcon(order.order_status)}
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.order_status)}`}>
                    {order.order_status.charAt(0).toUpperCase() + order.order_status.slice(1)}
                  </span>
                </div>
              </div>
              
              {/* Status Timeline */}
              <div className="space-y-4">
                <div className={`flex items-center space-x-3 ${order.order_status === 'pending' ? 'text-primary-600' : 'text-green-600'}`}>
                  <CheckCircleIcon className="h-5 w-5" />
                  <span className="font-medium">Order Placed</span>
                  <span className="text-sm text-gray-500">
                    {new Date(order.created_at).toLocaleDateString()}
                  </span>
                </div>
                
                {['confirmed', 'shipped', 'delivered'].includes(order.order_status) && (
                  <div className={`flex items-center space-x-3 ${order.order_status === 'confirmed' ? 'text-primary-600' : 'text-green-600'}`}>
                    <CheckCircleIcon className="h-5 w-5" />
                    <span className="font-medium">Order Confirmed</span>
                  </div>
                )}
                
                {['shipped', 'delivered'].includes(order.order_status) && (
                  <div className={`flex items-center space-x-3 ${order.order_status === 'shipped' ? 'text-primary-600' : 'text-green-600'}`}>
                    <TruckIcon className="h-5 w-5" />
                    <span className="font-medium">Order Shipped</span>
                  </div>
                )}
                
                {order.order_status === 'delivered' && (
                  <div className="flex items-center space-x-3 text-green-600">
                    <CheckCircleIcon className="h-5 w-5" />
                    <span className="font-medium">Order Delivered</span>
                  </div>
                )}
                
                {order.order_status === 'cancelled' && (
                  <div className="flex items-center space-x-3 text-red-600">
                    <XCircleIcon className="h-5 w-5" />
                    <span className="font-medium">Order Cancelled</span>
                  </div>
                )}
              </div>
            </div>

            {/* Order Tracking Timeline */}
            <OrderTrackingTimeline order={order} />

            {/* Order Items */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Items</h2>
              
              <div className="space-y-4">
                {order.items?.map((item) => (
                  <div key={item.id} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex-shrink-0">
                      <img
                        src={getPrimaryImage(item.product?.images)}
                        alt={item.product?.name || 'Product'}
                        className="w-16 h-16 object-cover rounded-md"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          if (!target.src.includes('via.placeholder.com')) {
                            target.src = getPlaceholderImage(64, 64, 'No Image');
                          }
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">{item.product?.name || 'Product'}</h3>
                      <div className="flex items-center space-x-4 mt-1">
                        <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                        <p className="text-sm text-gray-600">Unit Price: {formatCurrency(Number(item.unit_price || 0))}</p>
                      </div>
                      {item.product?.brand && (
                        <p className="text-xs text-gray-500 mt-1">Brand: {item.product.brand}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">
                        {formatCurrency(Number(item.total_price || 0))}
                      </p>
                      <p className="text-xs text-gray-500">Total</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Shipping Information */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Shipping Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-gray-900 mb-2 flex items-center">
                    <MapPinIcon className="h-4 w-4 mr-2" />
                    Shipping Address
                  </h3>
                  <p className="text-gray-600 whitespace-pre-line">{order.shipping_address}</p>
                </div>
                
                <div>
                  <h3 className="font-medium text-gray-900 mb-2 flex items-center">
                    <MapPinIcon className="h-4 w-4 mr-2" />
                    Billing Address
                  </h3>
                  <p className="text-gray-600 whitespace-pre-line">{order.billing_address}</p>
                </div>
              </div>
              
              <div className="mt-4">
                <h3 className="font-medium text-gray-900 mb-2 flex items-center">
                  <TruckIcon className="h-4 w-4 mr-2" />
                  Shipping Method
                </h3>
                <p className="text-gray-600 capitalize">
                  {order.shipping_method.replace('_', ' ')}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
              
              <div className="flex flex-wrap gap-3">
                {order.order_status === 'pending' && (
                  <Button 
                    variant="outline"
                    onClick={() => setShowCancelModal(true)}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <XCircleIcon className="h-4 w-4 mr-2" />
                    Cancel Order
                  </Button>
                )}
                
                {order.order_status === 'shipped' && (
                  <Button 
                    onClick={handleConfirmDelivery}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    Confirm Delivery
                  </Button>
                )}
                
                <Button 
                  variant="outline"
                  onClick={() => navigate(ROUTES.PRODUCTS)}
                >
                  Continue Shopping
                </Button>
              </div>
            </div>
            
            {/* Cancel Order Modal */}
            {showCancelModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-2" />
                      <h3 className="text-lg font-semibold text-gray-900">Cancel Order</h3>
                    </div>
                    
                    <p className="text-gray-600 mb-4">
                      Are you sure you want to cancel this order? This action cannot be undone.
                    </p>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reason for cancellation <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Please provide a reason for cancelling this order..."
                      />
                    </div>
                    
                    <div className="flex space-x-3">
                      <Button
                        onClick={handleCancelOrder}
                        className="flex-1 bg-red-600 hover:bg-red-700"
                        disabled={!cancelReason.trim()}
                      >
                        Confirm Cancellation
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowCancelModal(false);
                          setCancelReason('');
                        }}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border p-6 sticky top-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Order Number</span>
                  <span className="font-medium">{order.order_number}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Order Date</span>
                  <span className="font-medium">
                    {new Date(order.created_at).toLocaleDateString()}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Status</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    order.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {order.payment_status.charAt(0).toUpperCase() + order.payment_status.slice(1)}
                  </span>
                </div>
                
                <div className="border-t pt-3">
                  <div className="flex justify-between">
                    <span className="text-lg font-semibold text-gray-900">Total Amount</span>
                    <span className="text-lg font-semibold text-gray-900">
                      {formatCurrency(Number(order.total_amount || 0))}
                    </span>
                  </div>
                </div>

                {order.advance_amount && (
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Advance Paid</span>
                      <span className="font-medium">{formatCurrency(Number(order.advance_amount || 0))}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Remaining</span>
                      <span className="font-medium">
                        {formatCurrency(Number(order.total_amount || 0) - Number(order.advance_amount || 0))}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;
